import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Flex, Text, VisuallyHidden } from '@/components/ui';
import { useNavigate } from 'react-router-dom';
import { StudyDataImportDebugPanel } from './debug/StudyDataImportDebugPanel';

// Step components
import { FileUploadStep } from './steps/FileUploadStep';
import { FormDetectionStep } from './steps/FormDetectionStep';
import { PreviewAndValidationStep } from './steps/PreviewAndValidationStep';
import { ImportStep } from './steps/ImportStep';

// Progress bar component
import { StudyDataImportProgressBar } from './StudyDataImportProgressBar';

// API
import { createImportMapping } from '@/services/importMappingsApi';

// Utils
import { transformWizardStateToApiRequest } from '../utils/mappingTransformer';
import { calculateFileMappingStatus } from '../utils/mappingStatus';
import { buildFormTree } from './utils/formTreeUtils';

// Types
import { ImportWizardState, WizardStep, WizardStepStatus } from './types';
import { ValidationErrorDetails } from '@/components/data-import';
import { AxiosError } from 'axios';
import {
  IconArrowLeft,
  IconArrowRight,
  IconPlayerPlayFilled,
} from '@tabler/icons-react';

// Utils
import { toast } from '@/utils/toast';

interface StudyDataImportWizardProps {
  className?: string;
  initialStep?: WizardStep;
  wizardState: ImportWizardState;
  onUpdateWizardState: (updates: Partial<ImportWizardState>) => void;
  onResetWizard?: () => void;
  loadingForms?: boolean; // Loading state from parent component
}

const WIZARD_STEPS: Array<{
  id: WizardStep;
  title: string;
  description: string;
}> = [
  {
    id: 'file-upload',
    title: 'File Upload',
    description: 'Select CSV files for import',
  },
  {
    id: 'form-detection',
    title: 'Form Detection',
    description: 'Map files to EDC forms and columns',
  },
  {
    id: 'preview-and-validation',
    title: 'Preview & Validation',
    description: 'Review forms and validate data',
  },
  {
    id: 'import',
    title: 'Import',
    description: 'Execute the import process',
  },
];

export const StudyDataImportWizard: React.FC<StudyDataImportWizardProps> = ({
  className,
  initialStep = 'file-upload',
  wizardState,
  onUpdateWizardState,
  onResetWizard,
  loadingForms: loadingFormsProp = false,
}) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<WizardStep>(initialStep);
  const [isCreatingMapping, setIsCreatingMapping] = useState(false);

  // Use loading state from parent (StudyDataImportPage) which knows the real loading state
  // If prop is provided, use it (it's the real loading state from useStudyForms hook)
  // Otherwise, assume forms are not loading (fallback for backward compatibility)
  const loadingForms = loadingFormsProp;
  const [stepStatuses, setStepStatuses] = useState<
    Record<WizardStep, WizardStepStatus>
  >({
    'file-upload': 'current',
    'form-detection': 'pending',
    'preview-and-validation': 'pending',
    import: 'pending',
  });

  // Sync currentStep with initialStep when it changes
  useEffect(() => {
    if (initialStep !== currentStep) {
      setCurrentStep(initialStep);
      // Update step statuses based on the initial step
      const newStatuses: Record<WizardStep, WizardStepStatus> = {
        'file-upload': 'pending',
        'form-detection': 'pending',
        'preview-and-validation': 'pending',
        import: 'pending',
      };

      const initialStepIndex = WIZARD_STEPS.findIndex(
        step => step.id === initialStep
      );
      WIZARD_STEPS.forEach((step, index) => {
        if (index < initialStepIndex) {
          newStatuses[step.id] = 'completed';
        } else if (index === initialStepIndex) {
          newStatuses[step.id] = 'current';
        }
      });

      setStepStatuses(newStatuses);
    }
  }, [initialStep, currentStep]);

  // Forms are loaded centrally in StudyDataImportPage
  // No need to load forms here anymore

  // Helper function to get fileId from SelectedFile
  const getFileId = useCallback((file: unknown): string => {
    if (!file || typeof file !== 'object') {
      return 'unknown';
    }
    const f = file as {
      source?: unknown;
      file?: { name?: unknown };
      csvFile?: { id?: unknown };
    };
    return f.source === 'uploaded'
      ? String(f.file?.name ?? 'unknown')
      : `csv-${String(f.csvFile?.id ?? 'unknown')}`;
  }, []);

  // Calculate number of files with "Ready to Import!" status
  const readyToImportCount = useMemo(() => {
    const {
      selectedFiles,
      fileToFormMappings,
      availableForms,
      columnMappings,
    } = wizardState;

    // If no files or forms, return 0
    if (
      !selectedFiles.length ||
      !availableForms ||
      !availableForms.length ||
      !fileToFormMappings.length
    ) {
      return 0;
    }

    // Build form tree to determine if forms are subforms
    const formTree = buildFormTree(availableForms);
    const rootFormIds = new Set(formTree.map(root => root.id));

    // Create a map of forms by ID for quick lookup
    const formsMap = new Map<number, (typeof availableForms)[0]>();
    availableForms.forEach(form => {
      formsMap.set(form.id, form);
    });

    let readyCount = 0;

    // Check each file
    selectedFiles.forEach(file => {
      const fileId = getFileId(file);

      // Find file-to-form mapping
      const fileMapping = fileToFormMappings.find(m => m.fileId === fileId);
      if (!fileMapping) {
        return; // File not mapped, skip
      }

      // Find the form
      const selectedForm = formsMap.get(fileMapping.formId);
      if (!selectedForm) {
        return; // Form not found, skip
      }

      // Determine if form is a subform
      const isSelectedFormSubform = !rootFormIds.has(fileMapping.formId);

      // Calculate file mapping status
      const fileMappingStatus = calculateFileMappingStatus(
        selectedForm,
        file.metadata.columns,
        columnMappings || [],
        fileId,
        isSelectedFormSubform
      );

      // Count if status is 'excellent'
      if (fileMappingStatus.status === 'excellent') {
        readyCount++;
      }
    });

    return readyCount;
  }, [wizardState, getFileId]);

  const currentStepIndex = WIZARD_STEPS.findIndex(
    step => step.id === currentStep
  );
  const canGoNext = currentStepIndex < WIZARD_STEPS.length - 1;
  const canGoPrevious = currentStepIndex > 0;

  // updateStepStatuses was used in an earlier iteration; keep state transitions local for now.

  const handleCreateMapping = useCallback(async () => {
    if (currentStep !== 'preview-and-validation') return;

    setIsCreatingMapping(true);
    // Clear previous validation errors and old mapping ID
    // Always create a new mapping, don't use cached one
    onUpdateWizardState({
      validationErrors: undefined,
      importMappingId: undefined,
    });

    try {
      // Always create a new mapping (don't use cached importMappingId)
      const mappingRequest = transformWizardStateToApiRequest(wizardState);
      const createdMapping = await createImportMapping(mappingRequest);
      const mappingId = createdMapping.id;

      // Only navigate if mapping was successfully created and we got a new ID
      if (mappingId) {
        // Save the new mapping ID to wizard state
        onUpdateWizardState({ importMappingId: mappingId });

        // Navigate to mapping details page (backend will handle import execution)
        navigate(`/data-import/mapping/${mappingId}`);
        toast.success(
          'Mapping created successfully! Redirecting to details page...'
        );
      } else {
        throw new Error('Mapping creation succeeded but no ID was returned');
      }
    } catch (error) {
      console.error('Failed to create mapping:', error);

      // Handle validation errors from backend
      if (error instanceof AxiosError && error.response?.data) {
        const errorData = error.response.data;
        console.log('=== Error Response Debug ===');
        console.log('Full error response:', error.response);
        console.log('Error response data:', errorData);
        console.log('Error response data keys:', Object.keys(errorData));
        console.log('errorData.error:', errorData?.error);
        console.log('errorData.error?.details:', errorData?.error?.details);
        console.log('errorData.details:', errorData?.details);
        console.log('=== End Error Response Debug ===');

        // Try different possible error structures
        let errorDetails: ValidationErrorDetails | null = null;

        if (errorData?.error?.details) {
          console.log('Found errors in errorData.error.details');
          errorDetails = errorData.error.details as ValidationErrorDetails;
        } else if (errorData?.details) {
          console.log('Found errors in errorData.details');
          errorDetails = errorData.details as ValidationErrorDetails;
        } else if (errorData?.error && typeof errorData.error === 'object') {
          // Check if error itself contains column_mappings or other validation fields
          const errorObj = errorData.error;
          if (
            errorObj.column_mappings ||
            errorObj.file_to_form_mappings ||
            errorObj.subform_relationships
          ) {
            console.log('Found errors directly in errorData.error object');
            errorDetails = errorObj as ValidationErrorDetails;
          }
        }

        if (errorDetails) {
          console.log(
            'Setting validation errors in wizard state:',
            errorDetails
          );
          onUpdateWizardState({ validationErrors: errorDetails });
          toast.error(
            'Mapping validation failed. Please review the errors below and fix them.'
          );
          return; // Don't show generic error toast
        }
      }

      // Generic error handling
      toast.error('Failed to create mapping. Please try again.');
    } finally {
      setIsCreatingMapping(false);
    }
  }, [
    currentStep,
    wizardState,
    navigate,
    onUpdateWizardState,
  ]);

  const handleNext = useCallback(() => {
    if (canGoNext) {
      const nextStep = WIZARD_STEPS[currentStepIndex + 1];
      navigate(`/data-import/${nextStep.id}`);
    }
  }, [currentStepIndex, canGoNext, navigate]);

  const handlePrevious = useCallback(() => {
    if (canGoPrevious) {
      const previousStep = WIZARD_STEPS[currentStepIndex - 1];
      navigate(`/data-import/${previousStep.id}`);
    }
  }, [currentStepIndex, canGoPrevious, navigate]);

  const updateWizardState = onUpdateWizardState;

  const renderStepContent = () => {
    switch (currentStep) {
      case 'file-upload':
        return (
          <FileUploadStep
            wizardState={wizardState}
            onUpdateState={updateWizardState}
            onNext={handleNext}
            onPrevious={handlePrevious}
            canGoNext={canGoNext}
            canGoPrevious={canGoPrevious}
            currentStep={currentStepIndex + 1}
            totalSteps={WIZARD_STEPS.length}
            onResetWizard={onResetWizard}
          />
        );
      case 'form-detection':
        return (
          <FormDetectionStep
            wizardState={wizardState}
            onUpdateState={updateWizardState}
            onNext={handleNext}
            onPrevious={handlePrevious}
            canGoNext={canGoNext}
            canGoPrevious={canGoPrevious}
            currentStep={currentStepIndex + 1}
            totalSteps={WIZARD_STEPS.length}
            loadingForms={loadingForms}
          />
        );
      case 'preview-and-validation':
        return (
          <PreviewAndValidationStep
            wizardState={wizardState}
            onUpdateState={updateWizardState}
            onNext={handleNext}
            onPrevious={handlePrevious}
            canGoNext={canGoNext}
            canGoPrevious={canGoPrevious}
            currentStep={currentStepIndex + 1}
            totalSteps={WIZARD_STEPS.length}
          />
        );
      case 'import':
        return (
          <ImportStep
            wizardState={wizardState}
            onUpdateState={updateWizardState}
            onNext={handleNext}
            onPrevious={handlePrevious}
            canGoNext={canGoNext}
            canGoPrevious={canGoPrevious}
            currentStep={currentStepIndex + 1}
            totalSteps={WIZARD_STEPS.length}
          />
        );
      default:
        return null;
    }
  };

  const handleStartNew = useCallback(() => {
    if (onResetWizard) {
      onResetWizard();
    }
    // Reset step statuses
    setStepStatuses({
      'file-upload': 'current',
      'form-detection': 'pending',
      'preview-and-validation': 'pending',
      import: 'pending',
    });
    navigate('/data-import/file-upload');
  }, [navigate, onResetWizard]);

  return (
    <>
      <Flex
        direction="column"
        gap="2"
        style={{ height: 'inherit' }}
        className={className}
      >
        {/* Step Content */}
        {renderStepContent()}

        {/* Navigation Controls */}
        <Flex
          align="center"
          style={{
            display: 'grid',
            gridTemplateColumns: '150px 1fr 150px',
            gap: '16px',
            justifyContent: 'center',
            marginTop: 'var(--space-3)',
            paddingTop: 'var(--space-4)',
            boxShadow: '0 -1px 0 0 var(--gray-3)',
            position: 'sticky',
            bottom: 0,
            background: 'var(--color-background)',
            zIndex: 3,
            paddingBottom: 'var(--space-2)',
          }}
        >
          <Flex>
            <Button
              variant={canGoPrevious ? 'outlined' : 'contained'}
              onClick={handlePrevious}
              disabled={!canGoPrevious}
              startIcon={<IconArrowLeft />}
            >
              Previous
            </Button>
          </Flex>
          <Flex justify="center">
            {/* Progress Bar */}
            <StudyDataImportProgressBar
              currentStep={currentStep}
              stepStatuses={stepStatuses}
              onStartNew={handleStartNew}
              readyToImportCount={readyToImportCount}
              totalFilesCount={wizardState.selectedFiles.length}
            />
            <VisuallyHidden>
              <Text size="2xsmall" color="gray" align="center">
                Step {currentStepIndex + 1} of {WIZARD_STEPS.length}
              </Text>
            </VisuallyHidden>
          </Flex>

          <Flex justify="end">
            {currentStep === 'preview-and-validation' ? (
              <Button
                variant="classic"
                color="success"
                onClick={handleCreateMapping}
                disabled={isCreatingMapping}
                loading={isCreatingMapping}
                endIcon={<IconPlayerPlayFilled />}
              >
                {isCreatingMapping ? 'Creating Mapping...' : 'Create Mapping'}
              </Button>
            ) : (
              <Button
                variant="classic"
                color="primary"
                onClick={handleNext}
                disabled={!canGoNext}
                endIcon={<IconArrowRight />}
              >
                Next
              </Button>
            )}
          </Flex>
        </Flex>
      </Flex>

      {/* Study Data Import Debug Panel */}
      <StudyDataImportDebugPanel wizardState={wizardState} />
    </>
  );
};

export default StudyDataImportWizard;
